import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  FileText, Plus, Trash2,
  Send, Download, Check, Clock, Pen,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  sent: 'bg-blue-500/20 text-blue-400',
  viewed: 'bg-yellow-500/20 text-yellow-400',
  signed: 'bg-green-500/20 text-green-400',
};

const statusLabels = {
  draft: 'Szkic',
  sent: 'Wyslany',
  viewed: 'Przegladany',
  signed: 'Podpisany',
};

export default function ContractsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContract, setShowNewContract] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [newContract, setNewContract] = useState({
    client_name: '', client_email: '', event_date: '', event_type: '',
    price: '', currency: 'PLN', template_id: '', custom_content: '',
  });

  const [newTemplate, setNewTemplate] = useState({
    name: '', content: '', variables: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/contracts/templates`),
      ]);
      setContracts(contractsRes.data.contracts);
      setTemplates(templatesRes.data.templates);
    } catch (error) {
      toast.error('Blad ladowania danych');
    } finally {
      setLoading(false);
    }
  };

  const createContract = async () => {
    if (!newContract.client_name || !newContract.client_email) {
      toast.error('Wpisz nazwe i email klienta');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...newContract,
        price: newContract.price ? parseFloat(newContract.price) : null,
        template_id: newContract.template_id || null,
      };
      await axios.post(`${API}/contracts`, payload);
      toast.success('Umowa utworzona');
      setShowNewContract(false);
      setNewContract({ client_name: '', client_email: '', event_date: '', event_type: '', price: '', currency: 'PLN', template_id: '', custom_content: '' });
      fetchData();
    } catch (error) {
      toast.error('Blad tworzenia umowy');
    } finally {
      setCreating(false);
    }
  };

  const createTemplate = async () => {
    if (!newTemplate.name || !newTemplate.content) {
      toast.error('Wpisz nazwe i tresc szablonu');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/contracts/templates`, newTemplate);
      toast.success('Szablon utworzony');
      setShowNewTemplate(false);
      setNewTemplate({ name: '', content: '', variables: [] });
      fetchData();
    } catch (error) {
      toast.error('Blad tworzenia szablonu');
    } finally {
      setCreating(false);
    }
  };

  const sendContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/send`);
      toast.success('Umowa wyslana do klienta');
      fetchData();
    } catch (error) {
      toast.error('Blad wysylania umowy');
    }
  };

  const downloadPdf = async (contractId, clientName) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `umowa-${clientName.replace(/\s/g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Blad generowania PDF');
    }
  };

  const deleteContract = async (contractId) => {
    if (!window.confirm('Na pewno usunac umowe?')) return;
    try {
      await axios.delete(`${API}/contracts/${contractId}`);
      toast.success('Umowa usunieta');
      fetchData();
    } catch (error) {
      toast.error('Blad usuwania');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/contracts" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold mb-1">{language === 'pl' ? 'Umowy' : 'Contracts'}</h1>
            <p className="text-muted-foreground">{language === 'pl' ? 'Tworzenie, wysylanie i podpisywanie umow' : 'Create, send and sign contracts'}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowNewTemplate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Nowy szablon' : 'New Template'}
            </Button>
            <Button onClick={() => setShowNewContract(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Nowa umowa' : 'New Contract'}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Wszystkie', value: contracts.length, icon: FileText },
            { label: 'Szkice', value: contracts.filter(c => c.status === 'draft').length, icon: Pen },
            { label: 'Oczekujace', value: contracts.filter(c => ['sent', 'viewed'].includes(c.status)).length, icon: Clock },
            { label: 'Podpisane', value: contracts.filter(c => c.status === 'signed').length, icon: Check },
          ].map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <Card>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Contracts List */}
        <div className="space-y-3">
          {contracts.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{language === 'pl' ? 'Brak umow. Utwroz pierwsza umowe!' : 'No contracts yet. Create your first one!'}</p>
              </CardContent>
            </Card>
          ) : contracts.map((contract, i) => (
            <motion.div key={contract.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="hover:border-primary/30 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold">{contract.client_name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${statusColors[contract.status]}`}>
                          {statusLabels[contract.status]}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{contract.client_email}</span>
                        {contract.event_date && <span>{contract.event_date}</span>}
                        {contract.event_type && <span>{contract.event_type}</span>}
                        {contract.price && <span>{contract.price} {contract.currency}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {contract.status === 'draft' && (
                        <Button size="sm" variant="outline" onClick={() => sendContract(contract.id)}>
                          <Send className="w-4 h-4 mr-1" />Wyslij
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(contract.id, contract.client_name)}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteContract(contract.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* New Contract Dialog */}
        <Dialog open={showNewContract} onOpenChange={setShowNewContract}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'pl' ? 'Nowa umowa' : 'New Contract'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Imie i nazwisko klienta</Label>
                  <Input value={newContract.client_name} onChange={e => setNewContract(p => ({ ...p, client_name: e.target.value }))} placeholder="Jan Kowalski" />
                </div>
                <div>
                  <Label>Email klienta</Label>
                  <Input value={newContract.client_email} onChange={e => setNewContract(p => ({ ...p, client_email: e.target.value }))} placeholder="jan@email.com" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data wydarzenia</Label>
                  <Input type="date" value={newContract.event_date} onChange={e => setNewContract(p => ({ ...p, event_date: e.target.value }))} />
                </div>
                <div>
                  <Label>Typ sesji</Label>
                  <Input value={newContract.event_type} onChange={e => setNewContract(p => ({ ...p, event_type: e.target.value }))} placeholder="Wesele, Portret..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kwota</Label>
                  <Input type="number" value={newContract.price} onChange={e => setNewContract(p => ({ ...p, price: e.target.value }))} placeholder="3500" />
                </div>
                <div>
                  <Label>Waluta</Label>
                  <Input value={newContract.currency} onChange={e => setNewContract(p => ({ ...p, currency: e.target.value }))} />
                </div>
              </div>
              {templates.length > 0 && (
                <div>
                  <Label>Szablon</Label>
                  <select
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                    value={newContract.template_id}
                    onChange={e => setNewContract(p => ({ ...p, template_id: e.target.value }))}
                  >
                    <option value="">Bez szablonu</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label>Tresc umowy (opcjonalnie)</Label>
                <Textarea
                  value={newContract.custom_content}
                  onChange={e => setNewContract(p => ({ ...p, custom_content: e.target.value }))}
                  placeholder="Tresc umowy... Uzyj {{client_name}}, {{event_date}}, {{price}} jako zmiennych."
                  rows={6}
                />
              </div>
              <Button onClick={createContract} disabled={creating} className="w-full">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Utworz umowe
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* New Template Dialog */}
        <Dialog open={showNewTemplate} onOpenChange={setShowNewTemplate}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'pl' ? 'Nowy szablon umowy' : 'New Contract Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nazwa szablonu</Label>
                <Input value={newTemplate.name} onChange={e => setNewTemplate(p => ({ ...p, name: e.target.value }))} placeholder="Umowa wesele standard" />
              </div>
              <div>
                <Label>Tresc szablonu</Label>
                <Textarea
                  value={newTemplate.content}
                  onChange={e => setNewTemplate(p => ({ ...p, content: e.target.value }))}
                  placeholder="Umowa zawarta dnia {{date_today}} pomiedzy {{client_name}} a fotografem..."
                  rows={10}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Zmienne: {'{{client_name}}'}, {'{{event_date}}'}, {'{{price}}'}, {'{{currency}}'}, {'{{date_today}}'}, {'{{event_type}}'}
                </p>
              </div>
              <Button onClick={createTemplate} disabled={creating} className="w-full">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Utworz szablon
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
